import { WORDLE_WORDS } from "./wordleWords";

export function getWordleFailureReason({
  guess1,
  result,
  guess2,
}: {
  guess1: string;
  result: string;
  guess2: string;
}) {
  if (!WORDLE_WORDS.has(guess2.toLowerCase())) {
    return `${guess2.toUpperCase()} is not a word`;
  }
  const charToMinimumOccurences: Record<string, number> = {};
  const charToMaximumOccurences: Record<string, number> = {};

  for (let i = 0; i < guess1.length; i++) {
    const char = guess1[i]?.toUpperCase();
    const resultForChar = result[i];
    switch (resultForChar) {
      case "G":
      case "Y":
        charToMinimumOccurences[char] ??= 0;
        charToMinimumOccurences[char]++;
    }
  }

  for (let i = 0; i < guess1.length; i++) {
    const char = guess1[i]?.toUpperCase();
    // if there's a B, then minimumOccurences === maximumOccurences
    if (result[i] === "B" && charToMinimumOccurences[char] != null) {
      charToMaximumOccurences[char] = charToMinimumOccurences[char];
    }
  }

  // check greens
  for (let i = 0; i < guess1.length; i++) {
    const guess1Char = guess1[i]?.toUpperCase();
    const guess2Char = guess2[i]?.toUpperCase();
    if (result[i] === "G" && guess1Char !== guess2Char) {
      return `${getWordForPosition(i)} letter must be ${guess1Char}`;
    }
  }

  // check blacks
  for (let i = 0; i < guess1.length; i++) {
    const guess1Char = guess1[i]?.toUpperCase();
    const guess2Char = guess2[i]?.toUpperCase();
    if (result[i] === "B") {
      // if guess1Char is in charToMinimumOccurences, then it
      // appears elsewhere in guess1 as a Y or a G
      if (guess1Char in charToMinimumOccurences) {
        if (guess1Char === guess2Char) {
          return `${getWordForPosition(i)} letter cannot be ${guess1Char}`;
        }
      }
      // using else here so that we ignore cases where it appears
      // elsewhere as a Y or a G. it'll be caught later by the occurences
      // check below
      else if (guess2.toUpperCase().includes(guess1Char)) {
        return `There is no ${guess1Char.toUpperCase()} in the solution`;
      }
    }
  }

  // check yellow
  for (let i = 0; i < guess1.length; i++) {
    const guess1Char = guess1[i]?.toUpperCase();
    const guess2Char = guess2[i]?.toUpperCase();
    if (result[i] === "Y" && guess1Char === guess2Char) {
      return `${getWordForPosition(i)} letter cannot be ${guess1Char}`;
    }
  }

  for (const [char, minimumOccurences] of Object.entries(
    charToMinimumOccurences
  )) {
    const actualOccurences = countOccurencesInString({
      character: char,
      string: guess2,
    });
    if (actualOccurences < minimumOccurences) {
      return `Missing ${char.toUpperCase()}`;
    }
  }

  for (const [char, maximumOccurences] of Object.entries(
    charToMaximumOccurences
  )) {
    const actualOccurences = countOccurencesInString({
      character: char,
      string: guess2,
    });
    if (actualOccurences > maximumOccurences) {
      return `Too many ${char.toUpperCase()}'s`;
    }
  }

  return undefined;
}

function getWordForPosition(position: number) {
  switch (position) {
    case 0:
      return "First";
    case 1:
      return "Second";
    case 2:
      return "Third";
    case 3:
      return "Fourth";
    case 4:
      return "Last";
  }
}

function countOccurencesInString({
  character,
  string,
}: {
  character: string;
  string: string;
}) {
  let count = 0;
  for (let i = 0; i < string.length; i++) {
    if (string[i].toUpperCase() === character.toUpperCase()) {
      count++;
    }
  }
  return count;
}
